import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import { alpha, useTheme } from '@mui/material/styles';

export default function AudioPlayerBar({
  audioRef,
  audioUrl,
  reducedMotion = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const placeholderRef = useRef(null);
  const [floating, setFloating] = useState(false);

  useEffect(() => {
    const node = placeholderRef.current;
    if (!node || !('IntersectionObserver' in window)) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      setFloating(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    }, { threshold: 0.01 });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const transition = reducedMotion
    ? 'none'
    : 'transform 260ms ease, opacity 260ms ease, box-shadow 260ms ease, border-color 180ms ease';

  return (
    <Box ref={placeholderRef} sx={{ minHeight: floating ? 76 : 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          boxSizing: 'border-box',
          p: { xs: 1, sm: floating ? 1.25 : 1.5 },
          borderRadius: floating ? 3 : 2,
          width: '100%',
          maxWidth: floating ? (isMobile ? 'calc(100vw - 24px)' : 760) : '100%',
          overflow: 'hidden',
          position: floating ? 'fixed' : 'relative',
          transition,
          opacity: floating ? 0.98 : 1,
          border: '1px solid',
          borderColor: floating
            ? alpha(theme.palette.primary.main, 0.32)
            : alpha(theme.palette.text.primary, 0.12),
          bgcolor: alpha(theme.palette.background.paper, floating ? 0.94 : 0.86),
          backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.9)}, ${alpha(theme.palette.primary.main, 0.035)})`,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: '0 auto auto 0',
            width: '100%',
            height: 3,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.light, 0.45)})`,
            opacity: floating ? 1 : 0.7,
          },
          ...(floating && {
            left: '50%',
            bottom: { xs: 12, sm: 18 },
            transform: 'translateX(-50%)',
            zIndex: theme.zIndex.modal - 1,
            boxShadow: `0 18px 40px ${alpha(theme.palette.text.primary, 0.2)}`,
            backdropFilter: 'blur(14px)',
          }),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }}>
          <Box
            sx={{
              display: { xs: floating ? 'none' : 'flex', sm: 'flex' },
              width: 38,
              height: 38,
              borderRadius: 2,
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
            }}
          >
            <GraphicEqIcon fontSize="small" />
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'block' }, flexShrink: 0, width: 92 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Wiedergabe
            </Typography>
            <Typography variant="body2" fontWeight={700} color="text.primary">
              Aufnahme
            </Typography>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <audio
              ref={audioRef}
              controls
              src={audioUrl}
              style={{
                width: '100%',
                maxWidth: '100%',
                display: 'block',
                height: floating ? 40 : 44,
              }}
            />
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
